import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Text from 'core/elements/Text'
import Button from 'core/elements/button'
import colors from 'core/themes/base/colors'
import Theme from 'core/themes/model'
import { useCallback, useMemo } from 'react'
import { BLUEPRINT_TABS } from './constants'

const useStyles = makeStyles((theme: Theme) => ({
  bannerContainer: {
    backgroundColor: '#E9F0F7',
    marginBottom: theme.spacing(3),
  },
  bannerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  bannerLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    flex: 1,
    marginLeft: theme.spacing(1),
  },
  bannerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  stepNumber: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    border: `1px solid ${theme.palette.grey[200]}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 600,
    flexShrink: 0,
  },
  bannerContent: {
    display: 'grid',
    gridTemplateRows: 'auto auto',
    gap: theme.spacing(1),
  },
  bannerTitle: {
    fontWeight: 600,
  },
  bannerMessage: {
    color: theme.palette.grey[700],
  },
  progressBarContainer: {
    display: 'flex',
    gap: theme.spacing(0.5),
    width: '100%',
  },
  progressSegment: {
    flex: 1,
    height: 6,
    borderRadius: 3,
  },
  errors: {
    display: 'grid',
    gap: theme.spacing(1),
  },
  submitWarning: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  warningColor: {
    color: theme.palette.yellow.main,
  },

  bannerIcon: {
    width: 24,
    height: 24,
    color: theme.palette.green['700'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '3px',
  },
  errorBackground: {
    backgroundColor: theme.palette.red[100],
  },
  errorIcon: {
    color: theme.palette.red[500],
  },
}))

interface BlueprintProgressBannerProps {
  activeTab: string
  hasBasicConfig: boolean
  hasHostConfig: boolean
  hasStorageConfig: boolean
  hasClusterDefaults: boolean
  showSaveButton?: boolean
  blueprintSaved?: boolean
  errorMessages?: string[]
  onSave?: () => void
  handleTabChange?: (tab: string) => void
  isUserSavingBlueprintFirstTime?: boolean
}

const stepMap = {
  [BLUEPRINT_TABS.BASIC_CONFIGS.value]: {
    stepNumber: 1,
    title: 'Configure Basic Settings',
    message:
      'Start by setting up your cluster blueprint name and DNS domain. Remember to save the blueprint after making any changes.',
  },
  [BLUEPRINT_TABS.HOST_CONFIGS.value]: {
    stepNumber: 2,
    title: 'Configure Host and Network Settings',
    message:
      'Next, configure the host configurations to proceed. Remember to save the blueprint after making any changes.',
  },
  [BLUEPRINT_TABS.PERSISTENT_STORAGE_CONNECTIVITY.value]: {
    stepNumber: 3,
    title: 'Configure Persistent Storage',
    message:
      'Set up storage volume backends for persistent storage. Remember to save the blueprint after making any changes.',
  },
  [BLUEPRINT_TABS.CLUSTER_DEFAULTS.value]: {
    stepNumber: 4,
    title: 'Customize Cluster Defaults',
    message:
      'Finally, configure the cluster default settings. Remember to save the blueprint after making any changes.',
  },
}

export default function BlueprintProgressBanner({
  activeTab,
  hasBasicConfig,
  hasHostConfig,
  hasStorageConfig,
  hasClusterDefaults,
  showSaveButton = false,
  blueprintSaved = false,
  errorMessages = [],
  onSave,
  handleTabChange,
  isUserSavingBlueprintFirstTime = false,
}: BlueprintProgressBannerProps) {
  const classes = useStyles({ isUserSavingBlueprintFirstTime })
  const allCompleted = hasBasicConfig && hasHostConfig && hasStorageConfig && hasClusterDefaults

  // Get current step information based on active tab
  const currentStepInfo = useMemo(() => {
    if (!isUserSavingBlueprintFirstTime) {
      return {
        stepNumber: 5,
        title: 'Cluster Blueprint Configuration',
        message: (
          <>
            <Text variant="body2" className={classes.bannerMessage}>
              Configure network, host, storage, and default settings for your cluster blueprint.
            </Text>
            <Text variant="body2" className={classes.bannerMessage}>
              This blueprint establishes your desired cluster configuration and ensures that cluster
              capacity added over time conforms to these settings.
            </Text>
          </>
        ),
      }
    }

    if (allCompleted) {
      return {
        stepNumber: 5,
        title: 'Congratulations!',
        message:
          'The cluster blueprint has been successfully set up. You can always return and update any configuration as needed.',
      }
    }

    return stepMap[activeTab] || stepMap[BLUEPRINT_TABS.BASIC_CONFIGS.value]
  }, [activeTab, allCompleted, isUserSavingBlueprintFirstTime])

  // Get color for each progress segment
  const getSegmentColor = (segmentIndex: number) => {
    const currentStep = currentStepInfo.stepNumber
    const completionStatuses = [hasBasicConfig, hasHostConfig, hasStorageConfig, hasClusterDefaults]
    const isCompleted = completionStatuses[segmentIndex]

    // Check if all previous steps are completed
    const allPreviousCompleted = completionStatuses.slice(0, segmentIndex).every((status) => status)

    if (isCompleted && allPreviousCompleted) {
      return colors.green['700'] // Green for completed (only if all previous steps are done)
    } else if (segmentIndex + 1 === currentStep) {
      return colors.blue['500'] // Blue for current
    } else {
      return colors.grey['000'] // Grey for not started or blocked by previous incomplete step
    }
  }

  // Show next button if no blueprint and not on last step
  const showNextButton = useMemo(
    () => isUserSavingBlueprintFirstTime && currentStepInfo.stepNumber < 4,
    [isUserSavingBlueprintFirstTime, currentStepInfo.stepNumber],
  )

  const goToNextStep = useCallback(() => {
    if (currentStepInfo.stepNumber === 1) {
      handleTabChange(BLUEPRINT_TABS.HOST_CONFIGS.value)
    } else if (currentStepInfo.stepNumber === 2) {
      handleTabChange(BLUEPRINT_TABS.PERSISTENT_STORAGE_CONNECTIVITY.value)
    } else if (currentStepInfo.stepNumber === 3) {
      handleTabChange(BLUEPRINT_TABS.CLUSTER_DEFAULTS.value)
    }
  }, [currentStepInfo, handleTabChange])

  return (
    <div
      className={clsx(classes.bannerContainer, errorMessages?.length && classes.errorBackground)}
    >
      <div className={classes.bannerHeader}>
        <div className={classes.bannerLeft}>
          {errorMessages?.length ? (
            <>
              <FontAwesomeIcon className={clsx(classes.bannerIcon, classes.errorIcon)}>
                circle-xmark
              </FontAwesomeIcon>
              <div className={classes.bannerContent}>
                <Text variant="body1" className={classes.bannerTitle}>
                  An error occurred while saving the Blueprint.
                </Text>
                <Text variant="body2" className={classes.bannerMessage}>
                  {errorMessages[0]}
                </Text>
              </div>
            </>
          ) : (
            <>
              {isUserSavingBlueprintFirstTime &&
                (allCompleted ? (
                  <FontAwesomeIcon solid className={classes.bannerIcon}>
                    circle-check
                  </FontAwesomeIcon>
                ) : (
                  <div className={classes.stepNumber}>{currentStepInfo.stepNumber}</div>
                ))}
              <div className={classes.bannerContent}>
                <Text variant="body1" className={classes.bannerTitle}>
                  {currentStepInfo.title}
                </Text>
                <Text variant="body2" className={classes.bannerMessage}>
                  {currentStepInfo.message}
                </Text>
              </div>
            </>
          )}
        </div>
        {showNextButton ? (
          <Button
            type="submit"
            onClick={goToNextStep}
            data-testid="blueprint-next-button"
            rightIcon="chevron-right"
          >
            Next
          </Button>
        ) : showSaveButton ? (
          <div className={classes.bannerRight}>
            <Button type="submit" onClick={onSave} data-testid="save-blueprint-button">
              {blueprintSaved ? 'Blueprint Saved' : 'Save Blueprint'}
            </Button>
          </div>
        ) : null}
      </div>
      {
        <div className={classes.progressBarContainer}>
          {errorMessages?.length ? (
            <div
              className={classes.progressSegment}
              style={{ backgroundColor: colors.red['500'] }}
            />
          ) : isUserSavingBlueprintFirstTime ? (
            <>
              {[0, 1, 2, 3].map((index) => (
                <div
                  key={index}
                  className={classes.progressSegment}
                  style={{ backgroundColor: getSegmentColor(index) }}
                />
              ))}
            </>
          ) : null}
        </div>
      }
    </div>
  )
}
